import React, { useState } from "react";
import {
  Box,
  TextField,
  IconButton,
  Typography,
  Container,
  Card,
  CardContent,
  InputAdornment,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Place as PlaceIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
} from "@mui/icons-material";
import { useMutation, useQueryClient } from "react-query";
import { axiosInstance } from "src/network/adapter";
import { ApiEndPoints } from "src/network/endpoints";
import { useDispatch, useSelector } from "react-redux";
import UpgradePlanDialog from "src/@core/components/UpgradePlanDialog";
import { setShowSubscriptionPlanDialog } from "src/redux/slices/AuthSlice";
import useBusinessPermissions from "src/hooks/useBusinessPermissions";

const LocationList = ({
  businessLocationData,
  onAddLocation,
  onEditLocation,
  IsMaxView,
}) => {
  // const remaningLocationsAdd =selectedBusiness.
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const selectedBusinessId = useSelector(
    (state) => state.auth.selectedBusinessId
  );
  const user = useSelector((state) => state.auth.user);
  const { hasPermission, isOwnBusiness } = useBusinessPermissions();
  
  // Check if user can edit location profile (for edit button)
  const canEditLocation = hasPermission('editLocationProfile');
  
  // Check if user can add/delete locations
  // Owners (isOwnBusiness = true) can always add/delete
  // Team members (isOwnBusiness = false) cannot add/delete
  const canAddOrDeleteLocation = isOwnBusiness;

  const [searchQuery, setSearchQuery] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [searchCriteria, setSearchCriteria] = useState("name"); // Default criteria
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);

  const locations = businessLocationData?.locations || [];

  const selectedBusiness =
    user?.business?.filter(
      (business) => business._id === selectedBusinessId
    )[0] || null;

  const features = selectedBusiness?.features || [];

  const locationQuantity =
    features.find((feature) => feature.featureKey === "business_locations")
      ?.quantity || 0;

  const remaningLocationsAdd = locationQuantity - locations.length;

  // Handle search criteria change
  const handleCriteriaChange = (event) => {
    setSearchCriteria(event.target.value);
  };

  // Handle search query change
  const handleSearch = (event) => {
    setSearchQuery(event.target.value.toLowerCase());
  };

  // Get display address
  const getDisplayAddress = (address) => {
    return address || "No address provided";
  };

  // Filter locations based on selected criteria
  const filteredLocations = locations.filter((location) => {
    switch (searchCriteria) {
      case "name":
        return location.businessLocationName
          ?.toLowerCase()
          .includes(searchQuery);
      case "address":
        return location.physicalAddress?.toLowerCase().includes(searchQuery);
      case "number":
        return (
          location.phoneNumber &&
          location.phoneNumber.number?.toLowerCase().includes(searchQuery)
        );
      case "email":
        return location.emailAddress?.toLowerCase().includes(searchQuery);
      default:
        return false;
    }
  });

  // Handle delete location
  const handleDeleteLocation = (location) => {
    setSelectedLocation(location);
    setDeleteDialogOpen(true);
  };

  // Delete location mutation
  const deleteLocationMutation = useMutation({
    mutationFn: async () => {
      const response = await axiosInstance.delete(
        ApiEndPoints.BUSINESS_LOCATION.delete(selectedLocation._id)
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([
        "businessLocationData",
        selectedBusinessId,
      ]);
      setDeleteDialogOpen(false);
      setSelectedLocation(null);
    },
    onError: (error) => {
      console.error("Failed to delete location:", error);
    },
  });

  // Confirm delete
  const confirmDelete = () => {
    deleteLocationMutation.mutate();
  };

  // Handle upgrade plan
  const handleUpgradePlan = () => {
    // TODO: Implement upgrade functionality
    // This could navigate to subscription page, open subscription dialog, etc.
    // console.log("Upgrade plan clicked for business:", selectedBusinessId);
    // toast.success("Redirecting to upgrade page...");
    dispatch(
      setShowSubscriptionPlanDialog({
        show: true,
        businessId: selectedBusinessId,
        currentPlan: selectedBusiness?.planName,
      })
    );
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          flexDirection: IsMaxView ? "column" : "row",
          justifyContent: IsMaxView ? "flex-start" : "space-between",
          alignItems: IsMaxView ? "stretch" : "center",
          gap: IsMaxView ? 2 : 2,
          mb: 3,
        }}
      >
        {/* Search Bar */}
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flex: 1 }}>
          {/* Combined Select + Input Field */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              border: "1px solid #cbd5e0",
              borderRadius: "8px",
              overflow: "hidden",
              height: 40,
              bgcolor: "#fff",
              flex: 1,
            }}
          >
            {/* Dropdown */}
            <Select
              value={searchCriteria}
              onChange={handleCriteriaChange}
              variant="standard"
              disableUnderline
              sx={{
                px: 2,
                fontWeight: 600,
                fontSize: 14,
                height: "100%",
                display: "flex",
                alignItems: "center",
                borderRight: "1px solid #cbd5e0",
                "& svg": { color: "#444" },
              }}
            >
              <MenuItem value="name">name</MenuItem>
              <MenuItem value="address">address</MenuItem>
              <MenuItem value="number">phone number</MenuItem>
              <MenuItem value="email">email</MenuItem>
            </Select>

            {/* Search Icon */}
            <Box
              sx={{
                px: 1.5,
                display: "flex",
                alignItems: "center",
                color: "#aaa",
              }}
            >
              <SearchIcon fontSize="small" />
            </Box>

            {/* Input Field */}
            <Box
              component="input"
              type="text"
              placeholder="search Here"
              value={searchQuery}
              onChange={handleSearch}
              style={{
                border: "none",
                outline: "none",
                fontSize: 14,
                paddingRight: 12,
                flex: 1,
                backgroundColor: "transparent",
              }}
            />
          </Box>
        </Box>

        {/* Buttons: shown inline for normal, stacked below for IsMaxView */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: 1,
            mt: IsMaxView ? 1 : 0,
            justifyContent: IsMaxView ? "flex-start" : "flex-end",
          }}
        >
          {/* Search Button */}
          <Button
            variant="contained"
            sx={{
              height: 40,
              px: 3,
              textTransform: "none",
              bgcolor: "#7d3cff",
              "&:hover": { bgcolor: "#6930ca" },
              borderRadius: "8px",
              fontWeight: 600,
            }}
          >
            search
          </Button>

          {/* Add Location Button */}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              // console.log("selectedBusiness", selectedBusiness);

              const locationFeature = features.find(
                (feature) => feature.featureKey === "business_locations"
              );
              if (!locationFeature?.isFree && remaningLocationsAdd <= 0) {
                setUpgradeDialogOpen(true);
                return;
              } else {
                onAddLocation();
              }
            }}
            disabled={!canAddOrDeleteLocation}
            sx={{
              height: 40,
              px: 2,
              textTransform: "lowercase",
              bgcolor: canAddOrDeleteLocation ? "#6c00e6" : "#ccc",
              "&:hover": { bgcolor: canAddOrDeleteLocation ? "#5a00c2" : "#ccc" },
              borderRadius: "8px",
              fontWeight: 600,
              cursor: canAddOrDeleteLocation ? "pointer" : "not-allowed",
            }}
          >
            add location
          </Button>
        </Box>
      </Box>

      {/* Location Cards */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {filteredLocations.length > 0 ? (
          filteredLocations.map((location) => (
            <Card
              key={location._id}
              sx={{
                borderRadius: "12px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                position: "relative",
              }}
            >
              <CardContent sx={{ p: 3 }}>
                {/* Edit Button */}
                <IconButton
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 48,
                    bgcolor: canEditLocation ? "#f0f0f0" : "#f5f5f5",
                    "&:hover": { bgcolor: canEditLocation ? "#e0e0e0" : "#f5f5f5" },
                    opacity: canEditLocation ? 1 : 0.5,
                    cursor: canEditLocation ? "pointer" : "not-allowed",
                  }}
                  onClick={() => canEditLocation && onEditLocation(location)}
                  disabled={!canEditLocation}
                >
                  <EditIcon fontSize="small" />
                </IconButton>

                {/* Delete Button */}
                <IconButton
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    bgcolor: canAddOrDeleteLocation ? "#ffebee" : "#f5f5f5",
                    "&:hover": { bgcolor: canAddOrDeleteLocation ? "#ffcdd2" : "#f5f5f5" },
                    opacity: canAddOrDeleteLocation ? 1 : 0.5,
                    cursor: canAddOrDeleteLocation ? "pointer" : "not-allowed",
                  }}
                  onClick={() => canAddOrDeleteLocation && handleDeleteLocation(location)}
                  disabled={!canAddOrDeleteLocation}
                >
                  <DeleteIcon fontSize="small" color={canAddOrDeleteLocation ? "error" : "disabled"} />
                </IconButton>

                {/* Location Content */}
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Typography
                    variant="h6"
                    component="div"
                    sx={{ fontWeight: 600 }}
                  >
                    {location.businessLocationName}
                  </Typography>
                  {location.isPrimaryAddress && (
                    <Typography
                      variant="caption"
                      sx={{
                        ml: 2,
                        px: 1,
                        py: 0.5,
                        bgcolor: "#e3f2fd",
                        borderRadius: "4px",
                        color: "#1976d2",
                      }}
                    >
                      Primary Address
                    </Typography>
                  )}
                </Box>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <PlaceIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {getDisplayAddress(location.physicalAddress)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <EmailIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {location.emailAddress || "N/A"}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <PhoneIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {location.phoneNumber
                        ? `${location.phoneNumber.countryCode} ${location.phoneNumber.number}`
                        : "N/A"}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))
        ) : (
          <Typography color="text.secondary" align="center" mt={3}>
            No locations found.
          </Typography>
        )}
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>delete location</DialogTitle>
        <DialogContent>
          <Typography>
            are you sure you want to delete this location?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            variant="contained"
            sx={{
              textTransform: "lowercase",
            }}
          >
            cancel
          </Button>
          <Button
            onClick={confirmDelete}
            color="error"
            variant="contained"
            disabled={deleteLocationMutation.isLoading}
            sx={{
              textTransform: "lowercase",
            }}
          >
            {deleteLocationMutation.isLoading ? "deleting..." : "delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upgrade Plan Dialog */}
      <UpgradePlanDialog
        open={upgradeDialogOpen}
        onClose={() => setUpgradeDialogOpen(false)}
        selectedBusiness={selectedBusiness}
        locationQuantity={locationQuantity}
        onUpgrade={handleUpgradePlan}
      />
    </Container>
  );
};

export default LocationList;
